import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PaymentGatewayConnectionResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";
import { invalidateSellerPaymentGatewayConnection } from "@/lib/query/core/invalidation";
import type { GatewayConfigValue } from "@/modules/settings/components/integrations/paymentGatewayDescriptors";

// One set of hooks for every gateway (paymob | paytabs | kashier | fawry); the
// `provider` slug selects the backend adapter. Store-scoped via the seller
// session cookie — storeSlug is only in the query key for cache isolation.

type QueryParams = {
  storeSlug: string;
  provider: string;
  enabled?: boolean;
};

export function usePaymentGatewayConnectionQuery({
  storeSlug,
  provider,
  enabled = true,
}: QueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.paymentGatewayConnection(storeSlug, provider),
    queryFn: () =>
      apiJsonClient<PaymentGatewayConnectionResponse>(
        endpoints.seller.paymentGatewayConnection(provider),
      ),
    enabled: storeSlug.length > 0 && enabled,
  });

  return {
    connection: query.data?.connection ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

type MutationCallbacks = {
  onError?: (error: Error) => void;
};

export function useSavePaymentGatewayMutation(
  provider: string,
  callbacks?: MutationCallbacks,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      storeSlug: string;
      config: Record<string, GatewayConfigValue>;
    }) =>
      apiJsonClient<PaymentGatewayConnectionResponse>(
        endpoints.seller.paymentGatewayConnection(provider),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input.config),
        },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateSellerPaymentGatewayConnection(
        queryClient,
        variables.storeSlug,
        provider,
      );
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useUpdatePaymentGatewayActiveMutation(
  provider: string,
  callbacks?: MutationCallbacks,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: { storeSlug: string; isActive: boolean }) =>
      apiJsonClient<PaymentGatewayConnectionResponse>(
        endpoints.seller.paymentGatewayActive(provider),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isActive: input.isActive }),
        },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateSellerPaymentGatewayConnection(
        queryClient,
        variables.storeSlug,
        provider,
      );
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

export function useDisconnectPaymentGatewayMutation(
  provider: string,
  callbacks?: MutationCallbacks,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: { storeSlug: string }) => {
      void input;
      return apiJsonClient<PaymentGatewayConnectionResponse>(
        endpoints.seller.paymentGatewayConnection(provider),
        { method: "DELETE" },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerPaymentGatewayConnection(
        queryClient,
        variables.storeSlug,
        provider,
      );
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
